import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from '../../../icons/icons';

interface IAccordian {
  title: string;
  rightIcons?: React.ReactNode; // Optional icons/buttons on the right side
  children: React.ReactNode; // Content inside the accordion body
  isExpanded?: boolean; // Optional external control for expanded state
  onToggle?: (isOpen: boolean) => void; // Callback for toggle state changes
}

export const EnhancedAccordian = (props: IAccordian) => {
  const { title, rightIcons = [], children, isExpanded, onToggle } = props;
  const [isOpen, setIsOpen] = useState(false);
  const controlled = isExpanded !== undefined;
  const openState = controlled ? isExpanded! : isOpen;

  const toggle = () => {
    if (!controlled) setIsOpen(!isOpen);
    onToggle?.(!openState);
  };

  return (
    <div
        className={`mb-2 overflow-hidden rounded-lg bg-white transition-all duration-200 border
          ${
            openState
              ? 'border-emerald-200 shadow-sm ring-1 ring-emerald-500/10'
              : 'border-slate-100 hover:border-emerald-100 hover:shadow-sm'
          }`}
    >
      <div
          className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2.5 transition-colors
            ${openState ? 'border-b border-slate-100 bg-emerald-50/20' : ''}`}
          onClick={toggle}
      >
        <div className='flex min-w-0 items-center gap-2'>
          <div
              className={`h-5 w-1 shrink-0 rounded-full transition-colors duration-300 ${
                openState ? 'bg-emerald-500' : 'bg-slate-200'
              }`}
          />
          <div className='truncate text-sm font-semibold tracking-tight text-slate-900'>
            { title }
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          { React.Children.map(rightIcons, (icon, idx) => {
            const variant = (icon as any).props?.['data-variant'];
            const isDanger = variant === 'danger';
            return (
              <button
                  key={idx}
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation(); // prevent the toggle
                    const handler = (icon as any).props.onClick;
                    handler && handler(e);
                  }}
                  className={`flex h-8 w-8 items-center justify-center rounded-md border transition-all duration-200 ${
                    isDanger
                      ? 'border-red-100 bg-red-50 text-red-600 hover:border-red-200 hover:bg-red-100'
                      : 'border-transparent text-slate-500 hover:border-emerald-100 hover:bg-emerald-50 hover:text-emerald-700'
                  }`}
              >
                <div className='flex h-4 w-4 items-center justify-center [&_svg]:h-4 [&_svg]:w-4'>
                  { icon }
                </div>
              </button>
            );
          }) }
          <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              className={`flex h-8 w-8 items-center justify-center rounded-md transition-all duration-200 ${
                openState
                  ? 'text-emerald-500 bg-emerald-50'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
          >
            <div className='flex h-4 w-4 items-center justify-center transition-transform duration-300 [&_svg]:h-4 [&_svg]:w-4'>
              { openState ? <ChevronUp /> : <ChevronDown /> }
            </div>
          </button>
        </div>
      </div>
      { openState && (
        <div
            className='bg-white p-3'
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
        >
          { children }
        </div>
      ) }
    </div>
  );
};
